import {
  Blockquote,
  Center,
  Container,
  Image,
  Stack,
  Text,
} from "@mantine/core";
import { useParams } from "wouter";
import { client } from "@/api-client";
import type { FileOutput } from "@/api-types";
import { FancyLoader } from "@/components/FancyLoader";
import { Header } from "@/components/Header";
import { MultilineText } from "@/components/MultilineText";
import { SurveyDefinitionViewer } from "@/components/SurveyDefinitionViewer";
import type { SurveyDefinition } from "@/utils/survey";

export interface FilePreviewProps {
  previewUrl: string;
  type: FileOutput["type"];
  mimeType?: string;
}

export const PreviewElement = (props: { file: FilePreviewProps }) => {
  const { file } = props;

  const previewStyle = {
    maxWidth: "100%",
    minHeight: "500px",
    height: "auto",
  };

  if (file.type === "VIDEO") {
    return <video controls src={file.previewUrl} style={previewStyle} />;
  }
  if (file.type === "IMAGE") {
    return <Image src={file.previewUrl} style={previewStyle} />;
  }
  if (file.type === "AUDIO") {
    return <audio controls src={file.previewUrl} style={previewStyle} />;
  }

  return (
    <embed src={file.previewUrl} type={file.mimeType} style={previewStyle} />
  );
};

export const FilePreview = () => {
  const { fileId } = useParams<{ fileId: string }>();

  const { data: file, isLoading } = client.useQuery(
    "get",
    "/universal/files/{fileId}",
    {
      params: {
        path: {
          fileId,
        },
      },
    },
  );

  if (isLoading) {
    return <FancyLoader />;
  }

  if (!file) {
    return (
      <Container>
        <Center>
          <Text>File loading or not found</Text>
        </Center>
      </Container>
    );
  }

  let fileMaybe: FilePreviewProps | null = null;
  if (typeof file.previewUrl === "string") {
    fileMaybe = {
      ...file,
      mimeType: file.type === "PDF" ? "application/pdf" : undefined,
    } as FilePreviewProps;
  }

  let contentMaybe: string | null = null;
  if (file.type === "NOTE") {
    contentMaybe = file.content;
  }

  let surveyMaybe: SurveyDefinition | null = null;
  if (file.type === "SURVEY" && file.content) {
    try {
      surveyMaybe = JSON.parse(file.content) as SurveyDefinition;
    } catch (e) {
      console.error("Failed to parse survey content", e);
    }
  }

  const something = contentMaybe || fileMaybe || surveyMaybe;

  const title =
    file.type === "NOTE"
      ? "Text Note"
      : file.type === "SURVEY"
        ? "Survey"
        : "File Preview";

  return (
    <Stack gap="xs">
      <Header title={title} />
      <Text size="lg" fw="bold">
        {file.name}
      </Text>
      <Text size="sm" c="dimmed">
        {file.description}
      </Text>
      {fileMaybe && (
        <>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <PreviewElement file={fileMaybe} />
          </div>
          <Text size="sm">Type: {file.type}</Text>
        </>
      )}
      {contentMaybe && (
        <Blockquote p="md">
          <MultilineText text={contentMaybe} />
        </Blockquote>
      )}
      {surveyMaybe && <SurveyDefinitionViewer definition={surveyMaybe} />}
      {!something && (
        <Text>This file has no content. This is probably a bug.</Text>
      )}
    </Stack>
  );
};
